import React, { useMemo } from 'react';
import isBoolean from 'lodash/isBoolean';

import {
  Typography,
  Table,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
  Actions,
  Action,
  Grid,
  makeStyles,
  ContentCopyIcon,
  Help,
  Stack,
  CheckIcon,
  RemoveIcon,
} from '@gogame/components';
import { DateValueFormatter } from '@gogame/intl-ts';

import { useCopyLink } from 'scenes/Campaigns/hooks';
import { DEEP_TYPE } from 'scenes/Campaigns/components/Form/constants';

import Info from './Info';
import InfoPopover from './InfoPopover';

const useStyles = makeStyles(() => ({
  text: {
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    'word-break': 'break-all',
  },
}));

const CampaignsLinksTable = ({ campaignLinks, withCopyParams = false }) => {
  const copyLink = useCopyLink();

  const classes = useStyles();

  const withDeepLinks = useMemo(
    () => campaignLinks.some(({ type }) => type?.id === DEEP_TYPE),
    [campaignLinks]
  );

  return (
    <TableContainer>
      <Table striped={true} rightStickyColumnCount={1}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 100 }}>Type</TableCell>
            <TableCell sx={{ minWidth: 400 }}>URL</TableCell>
            <TableCell sx={{ minWidth: 120 }}>Expiration date</TableCell>
            {withDeepLinks ? (
              <React.Fragment>
                <TableCell sx={{ minWidth: 140 }}>Reward package</TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  Multiple redemptions
                </TableCell>
                <TableCell sx={{ minWidth: 120 }}>Max redemptions</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Location in game</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Sub location</TableCell>
              </React.Fragment>
            ) : null}
            <TableCell sx={{ minWidth: 75 }}>Copy</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {campaignLinks.map(
            (
              { type, url, linkData = {}, additionalParams, expirationDate },
              i
            ) => {
              const {
                deeplinkValue,
                dueDate,
                rewardPackage,
                rewardPackageDescription,
                multipleRedemptions,
                maxRedemptions,
                locationInGame,
                subLocationInGame,
              } = linkData || {};
              const typeDeepLink = type?.id === DEEP_TYPE;

              return (
                <TableRow key={i}>
                  <TableCell size={'small'} width={100}>
                    {type?.name || '-'}
                  </TableCell>
                  <TableCell size={'small'}>
                    {url ? (
                      <Grid container alignItems={'center'} wrap={'nowrap'}>
                        <Grid item>
                          <Typography className={classes.text}>
                            {url}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <InfoPopover width={'400px'}>
                            <Info url={url} type={type} params={linkData} />
                          </InfoPopover>
                        </Grid>
                      </Grid>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell size={'small'}>
                    <DateValueFormatter
                      value={expirationDate || dueDate}
                      template={'LL'}
                      timeZone={'local'}
                    />
                  </TableCell>
                  {withDeepLinks ? (
                    typeDeepLink ? (
                      <React.Fragment>
                        <TableCell size={'small'}>
                          {rewardPackage ? (
                            <Stack direction={'row'} spacing={0.5}>
                              <span>{rewardPackage}</span>
                              <Help>{rewardPackageDescription}</Help>
                            </Stack>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell size={'small'} align={'center'}>
                          {isBoolean(multipleRedemptions) ? (
                            multipleRedemptions ? (
                              <CheckIcon fontSize={'small'} color={'action'} />
                            ) : (
                              <RemoveIcon fontSize={'small'} color={'action'} />
                            )
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell size={'small'}>
                          {maxRedemptions || '-'}
                        </TableCell>
                        <TableCell size={'small'}>
                          {locationInGame?.name || '-'}
                        </TableCell>
                        <TableCell size={'small'}>
                          {subLocationInGame?.name || '-'}
                        </TableCell>
                      </React.Fragment>
                    ) : (
                      <TableCell size={'small'} colSpan={5} />
                    )
                  ) : null}
                  <TableCell size={'small'}>
                    <Actions limit={1}>
                      {withCopyParams && (additionalParams || deeplinkValue) ? (
                        <Action icon={<ContentCopyIcon />}>
                          <Action
                            label={'Copy URL'}
                            onClick={() => copyLink(url)}
                          />
                          {additionalParams ? (
                            <Action
                              label={'Copy params'}
                              onClick={() => copyLink(additionalParams)}
                            />
                          ) : null}
                          {deeplinkValue ? (
                            <Action
                              label={'Copy link value'}
                              onClick={() => copyLink(deeplinkValue)}
                            />
                          ) : null}
                        </Action>
                      ) : (
                        <Action
                          icon={<ContentCopyIcon />}
                          label={'Copy URL'}
                          onClick={() => copyLink(url)}
                        />
                      )}
                    </Actions>
                  </TableCell>
                </TableRow>
              );
            }
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CampaignsLinksTable;
